import { IonContent, IonPage } from '@ionic/react';
import { useAuth } from '../auth/useAuth';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Btn, GameBadge, Icon } from '../components/chancey';
import { safeAreaPadding } from '../components/layout/safeArea';
import { RuleDetailSheet } from '../components/rules/RuleDetailSheet';
import { useGameApi } from '../api/games';
import type { RulesStatsResponse } from '../api/types';
import { RULE_PRESETS, SMART_PICK } from '../data/lotteryData';
import type { RuleId } from '../data/ruleSchema';
import { ruleBuilderService } from '../services/ruleBuilderService';
import { useRuleBuilderStore } from '../stores/ruleBuilderStore';
import type { TicketRule } from '../types/rules';
import type { ThemePalette, TypographyTokens } from '../theme/tokens';
import { useChanceyTheme } from '../theme/ThemeContext';
import { THEMES } from '../theme/tokens';
import { useActiveGame } from '../hooks/useActiveGame';
import { useUserPrefs } from '../hooks/useUserPrefs';
import { buildRulesStatsPayload } from '../rules/buildRulesStatsPayload';
import { previewPickIsValidForGame } from '../rules/previewPick';
import { RulesSimpleMode } from './rules/RulesSimpleMode';

function summarize(r: TicketRule): string {
  const s = r.settings || {};
  if (r.id === 'birthday') return `Month ${s.month} · day ${s.day}`;
  if (r.id === 'hot') return `${s.window} win · top ${s.topK} · min ${s.minHot}`;
  if (r.id === 'cold') return `${s.window} win · cold ${s.bottomK} · min ${s.minCold}`;
  if (r.id === 'overdue') return `≥${s.threshold} draws · min ${s.minOverdue}`;
  if (r.id === 'spread') return `${s.zones} areas · ${spreadStrengthLabel(s.strictness)}`;
  if (r.id === 'odd-even') return `${s.ratio} ratio`;
  return '';
}

function spreadStrengthLabel(value: unknown): string {
  if (value === 'loose') return 'Gentle';
  if (value === 'strict') return 'Wide';
  return 'Balanced';
}

/** Catalog short description (`RULE_PRESETS.desc`), or settings summary if missing. */
function ruleShortDescription(r: TicketRule): string {
  const preset = RULE_PRESETS.find((p) => p.id === r.id);
  return preset?.desc ?? summarize(r);
}

/**
 * (1 − eligible_after_this_rule ÷ eligible_after_previous_rules) × 100.
 * "Eligible after previous" is the full pool for the first rule, otherwise cumulative pool after the rule above.
 */
function stackStepNarrowingPercent(eligibleAfterRule: number, eligibleAfterPrevious: number): number | null {
  if (!Number.isFinite(eligibleAfterRule) || !Number.isFinite(eligibleAfterPrevious)) return null;
  if (eligibleAfterPrevious <= 0 || eligibleAfterRule <= 0) return null;
  if (eligibleAfterRule > eligibleAfterPrevious * 1.0001) return null;
  return (1 - eligibleAfterRule / eligibleAfterPrevious) * 100;
}

function formatNarrowingPct(pct: number): string {
  const abs = Math.abs(pct);
  const frac = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return pct.toLocaleString(undefined, { maximumFractionDigits: frac, minimumFractionDigits: 0 });
}

/** Client-only fallback for empty-rule stack (full game space). Not valid for constrained stacks. */
function sampleTicketLocal(game: {
  mainRange: number;
  mainCount: number;
  bonusRange: number;
  bonusCount: number;
}): { main: number[]; bonus: number[] } {
  const idx = new Set<number>();
  while (idx.size < game.mainCount) {
    idx.add(Math.floor(Math.random() * game.mainRange) + 1);
  }
  const main = Array.from(idx).sort((a, b) => a - b);
  const bonus = Array.from({ length: game.bonusCount }, () => Math.floor(Math.random() * game.bonusRange) + 1);
  return { main, bonus };
}

/** Design: surfaceAlt footer with sparkle, improvement %, and pool odds (matches screen-rules.jsx). */
function ruleCardImprovementFooter(
  eligibleAfterRule: number,
  eligibleAfterPrevious: number,
  method: 'exact' | 'mc',
  c: ThemePalette,
  typ: TypographyTokens
): React.ReactNode {
  const pct = stackStepNarrowingPercent(eligibleAfterRule, eligibleAfterPrevious);
  if (pct === null) return null;
  const approx = method === 'mc' ? '~' : '';
  const poolStr = `${approx}1 : ${eligibleAfterRule.toLocaleString()}`;
  return (
    <div
      className="flex items-center gap-2 rounded-[10px] px-2.5 py-2"
      style={{ background: c.surfaceAlt }}
    >
      <div className="shrink-0">{Icon.sparkle(14, c.accent)}</div>
      <div className="min-w-0 flex-1 text-[11px] leading-snug" style={{ fontFamily: typ.body, color: c.ink }}>
        Narrows pool vs previous step by{' '}
        <span style={{ fontFamily: typ.mono, color: c.accent, fontWeight: 700 }}>
          {approx}
          {formatNarrowingPct(pct)}%
        </span>
      </div>
      <div className="shrink-0 font-mono text-[11px] font-semibold" style={{ color: c.inkSoft, fontFamily: typ.mono }}>
        {poolStr}
      </div>
    </div>
  );
}

const RulesPage: React.FC = () => {
  const { prefs, setPartial } = useUserPrefs();
  const experienceMode = prefs.experienceMode;
  const { c, typ, d, dark, themeName } = useChanceyTheme();
  const { isSignedIn, isLoaded: authLoaded } = useAuth();
  const { postRulesStats } = useGameApi();
  const { game, gameId } = useActiveGame();
  const { rules, rulePresets, hasHydrated } = useRuleBuilderStore(
    useShallow((s) => ({
      rules: s.rules,
      rulePresets: s.rulePresets,
      hasHydrated: s.hasHydrated,
    })),
  );
  const [editingInstanceId, setEditingInstanceId] = useState<string | null>(null);
  const [generated, setGenerated] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const [stats, setStats] = useState<RulesStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [sheetStatsRefreshing, setSheetStatsRefreshing] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);

  /** Latest stats — ref lets the debounced effect read “do we have something to show?” without extra deps. */
  const statsRef = useRef<RulesStatsResponse | null>(null);
  statsRef.current = stats;
  /** Effect uses this so `postRulesStats` identity changes (Clerk token / useApi) don’t reset the 400ms debounce. */
  const postRulesStatsRef = useRef(postRulesStats);
  postRulesStatsRef.current = postRulesStats;

  const addRule = (id: RuleId) => ruleBuilderService.addRule(id);
  const removeRule = (instanceId: string) => ruleBuilderService.removeRule(instanceId);
  const updateRule = (instanceId: string, patch: Partial<TicketRule>) =>
    ruleBuilderService.updateRule(instanceId, patch);

  const available = rulePresets.filter((p) => !rules.find((r) => r.id === p.id));
  const editingRule = editingInstanceId ? rules.find((r) => r.instanceId === editingInstanceId) : undefined;
  const enabledRules = rules.filter((r) => r.enabled !== false);

  const stackWeightTotal = enabledRules.reduce((s, r) => s + r.weight, 0) || 1;

  useEffect(() => {
    if (experienceMode !== 'advanced') {
      return;
    }
    if (!authLoaded || !isSignedIn) {
      setStats(null);
      setStatsError(null);
      setStatsLoading(false);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      const hadCachedStats = statsRef.current !== null;
      if (!hadCachedStats) setStatsLoading(true);
      setStatsError(null);
      try {
        const res = await postRulesStatsRef.current(gameId, buildRulesStatsPayload(rules));
        if (!cancelled) {
          setStats(res);
          setStatsLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setStats(null);
          setStatsLoading(false);
          setStatsError(e instanceof Error ? e.message : 'Could not load rule stats');
        }
      }
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [experienceMode, authLoaded, isSignedIn, gameId, rules]);

  const refreshStatsWithDraftSettings = useCallback(
    async (draftSettings: Record<string, string | number | boolean>) => {
      if (!isSignedIn || !editingInstanceId) return;
      setSheetStatsRefreshing(true);
      try {
        const res = await postRulesStatsRef.current(
          gameId,
          buildRulesStatsPayload(rules, { draftInstanceId: editingInstanceId, draftSettings }),
        );
        setStats(res);
        setStatsError(null);
      } catch (e) {
        setStatsError(e instanceof Error ? e.message : 'Could not refresh stats');
      } finally {
        setSheetStatsRefreshing(false);
      }
    },
    [isSignedIn, editingInstanceId, rules, gameId]
  );

  /** Re-POST current stack to get a new `previewPick` (and stats), e.g. “Generate pick” or after add/save. */
  const refreshPreviewFromApi = useCallback(async () => {
    if (!authLoaded || !isSignedIn) return;
    setStatsError(null);
    try {
      const res = await postRulesStatsRef.current(gameId, buildRulesStatsPayload(rules));
      setStats(res);
    } catch (e) {
      setStatsError(e instanceof Error ? e.message : 'Could not load rule stats');
    }
  }, [authLoaded, isSignedIn, gameId, rules]);

  const displayPick = useMemo((): { main: number[]; bonus: number[] } => {
    const g = stats?.game;
    const pick = stats?.previewPick;
    if (g && pick && previewPickIsValidForGame(pick, g)) return pick;
    if (g && enabledRules.length === 0) {
      return sampleTicketLocal(g);
    }
    return { main: [...SMART_PICK.main], bonus: [...SMART_PICK.bonus] };
  }, [stats, enabledRules.length]);

  const previewAnimSignature = useMemo(
    () => (stats ? `${JSON.stringify(stats.previewPick ?? [])}:${stats.combined.remainingPoolSize}` : ''),
    [stats]
  );

  const previewAnimPrev = useRef<string | null>(null);
  useEffect(() => {
    if (!previewAnimSignature) return;
    if (previewAnimPrev.current === null) {
      previewAnimPrev.current = previewAnimSignature;
      return;
    }
    if (previewAnimPrev.current !== previewAnimSignature) {
      previewAnimPrev.current = previewAnimSignature;
      setAnimKey((k) => k + 1);
    }
  }, [previewAnimSignature]);

  return (
    <IonPage>
      <IonContent fullscreen style={{ '--background': c.bg }}>
        {experienceMode === 'simple' ? (
          <RulesSimpleMode onSwitchAdvanced={() => setPartial({ experienceMode: 'advanced' })} />
        ) : (
          <>
            <style>{`
          @keyframes revealPop {
            0% { transform: scale(0.4); opacity: 0; }
            60% { transform: scale(1.1); opacity: 1; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}</style>
            <div
              className="relative flex flex-col"
              style={{ ...safeAreaPadding(8, d.pad, 16, d.pad), gap: d.gap, color: c.ink }}
            >
          <div className="flex flex-col items-start gap-1.5 px-0.5 pb-0.5 pt-1">
            <GameBadge game={game} />
            <div
              className="text-[32px] leading-tight"
              style={{
                fontFamily: typ.display,
                fontWeight: typ.displayWeight,
                letterSpacing: typ.displayLetter,
              }}
            >
              Rule builder
            </div>
            <div className="mt-1.5 max-w-[320px] text-[13px] leading-snug" style={{ color: c.inkSoft, fontFamily: typ.body }}>
              Stack rules for your pick logic. When none are active, add one from the list at the bottom.
            </div>
          </div>

          <div
            className="relative overflow-hidden rounded-[24px] p-[18px] text-[color:var(--chancey-ink)]"
            style={{
              // `c.ink` flips to a light color in dark mode, so using it as a background
              // would make the preview card light-on-light. Pin the background to the
              // theme’s *light* ink (always dark) and the foreground to the current ink.
              background: dark ? THEMES[themeName].light.ink : c.ink,
              color: dark ? c.ink : c.bg,
            }}
          >
            <div className="absolute -right-3 -top-3 h-[60px] w-[60px] rounded-full opacity-25" style={{ background: c.accent }} />
            <div className="absolute -bottom-5 -left-5 h-20 w-20 rounded-full opacity-[0.12]" style={{ background: c.accent2 }} />
            <div className="relative mb-3.5 flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/70" style={{ fontFamily: typ.body }}>
                {generated ? 'Your pick' : 'Preview pick'}
              </div>
              <div
                className="font-mono text-[11px] font-semibold"
                style={{ color: 'currentColor', opacity: 0.85, fontFamily: typ.mono }}
              >
                {stats && !statsError ? (
                  <>
                    Pool 1:{` `}
                    {stats.combined.remainingPoolSize.toLocaleString()} ({stats.combined.method})
                  </>
                ) : (
                  <>{(SMART_PICK.confidence * 100).toFixed(0)}% match</>
                )}
              </div>
            </div>
            <div key={animKey} className="relative flex items-center gap-2" style={{ gap: 8 }}>
              {displayPick.main.map((n, i) => (
                <div
                  key={`${n}-${i}`}
                  className="flex shrink-0 items-center justify-center rounded-full font-mono text-[17px] font-bold shadow-lg"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 22,
                    background: c.bg,
                    color: c.ink,
                    fontFamily: typ.mono,
                    animation: `revealPop 0.4s ${i * 0.06}s cubic-bezier(.2,1.4,.3,1) both`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  }}
                >
                  {n}
                </div>
              ))}
              <div className="mx-0.5 h-7 w-px shrink-0 bg-white/20" style={{ width: 1, height: 28 }} />
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-mono text-[17px] font-bold text-white shadow-lg"
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  background: c.lucky,
                  fontFamily: typ.mono,
                  animation: `revealPop 0.4s ${Math.min(displayPick.main.length, 5) * 0.06}s cubic-bezier(.2,1.4,.3,1) both`,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                }}
              >
                {displayPick.bonus[0]}
              </div>
            </div>
            <div className="h-px bg-white/10" style={{ margin: '16px 0 12px' }} />
            <div className="flex items-center" style={{ gap: 10 }}>
              <div className="flex h-1.5 flex-1 overflow-hidden rounded-md" style={{ height: 6, borderRadius: 3 }}>
                {enabledRules.map((r, i) => {
                  const cols = [c.accent, c.accent2, c.accent3, '#fff', c.win];
                  const col = cols[i % 5];
                  const pct = (r.weight / stackWeightTotal) * 100;
                  return <div key={r.instanceId} style={{ width: `${pct}%`, background: col }} />;
                })}
              </div>
              <div className="shrink-0 font-mono text-[11px] text-white/70" style={{ fontFamily: typ.mono }}>
                {enabledRules.length} rules
              </div>
            </div>
          </div>

          <div className="mt-1">
            <Btn
              c={c}
              typ={typ}
              variant="lucky"
              style={{ width: '100%' }}
              onClick={() => {
                setGenerated(true);
                void refreshPreviewFromApi();
              }}
              icon={Icon.wand(18, '#fff')}
            >
              Generate pick
            </Btn>
          </div>

          <div className="flex items-baseline justify-between px-0.5 pt-1">
            <div
              className="text-[18px] leading-tight"
              style={{
                fontFamily: typ.display,
                fontWeight: typ.displayWeight,
                letterSpacing: typ.displayLetter,
                color: c.ink,
              }}
            >
              Active rules
            </div>
            <div className="text-[11px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
              Tap to configure
            </div>
          </div>

          <div className="flex flex-col" style={{ gap: 8 }}>
            {hasHydrated && rules.length === 0 && (
              <div
                className="rounded-2xl border px-4 py-5"
                style={{ background: c.surface, borderColor: c.line }}
                role="group"
                aria-label="Ideas for building your number rules"
              >
                <div
                  className="text-[11px] font-semibold uppercase tracking-[0.12em]"
                  style={{ color: c.accent, fontFamily: typ.body }}
                >
                  No rules yet
                </div>
                <div
                  className="mt-1 text-[19px] leading-tight"
                  style={{
                    fontFamily: typ.display,
                    fontWeight: typ.displayWeight,
                    letterSpacing: typ.displayLetter,
                    color: c.ink,
                  }}
                >
                  Add a few rules to build your numbers
                </div>

                <div className="mt-4 flex flex-col gap-2.5">
                  {[
                    { icon: Icon.flame(16, c.lucky), label: 'Hot', desc: 'Numbers that have been drawn a lot lately' },
                    { icon: Icon.clock(16, c.accent3), label: 'Overdue', desc: 'Numbers that have not shown up in a while' },
                    { icon: Icon.sparkle(16, c.accent), label: 'Spread', desc: 'Numbers spread from low to high across the board' },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center gap-2.5">
                      <div
                        aria-hidden="true"
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                        style={{ background: c.surfaceAlt }}
                      >
                        {row.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div
                          className="text-[13px] font-semibold"
                          style={{ color: c.ink, fontFamily: typ.body }}
                        >
                          {row.label}
                        </div>
                        <div
                          className="text-[12px] leading-snug"
                          style={{ color: c.inkSoft, fontFamily: typ.body }}
                        >
                          {row.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  className="mt-4 text-[12px] leading-snug"
                  style={{ color: c.inkMuted, fontFamily: typ.body }}
                >
                  Full auto-picks are coming soon. Add rules below to try the sample pick at the top of this screen.
                </div>

                <div className="mt-3 flex justify-center">
                  <div
                    aria-hidden="true"
                    style={{
                      color: c.accent,
                      transform: 'rotate(90deg)',
                      animation: 'revealPop 1.6s ease-in-out infinite alternate',
                    }}
                  >
                    {Icon.arrow(18, c.accent)}
                  </div>
                </div>
              </div>
            )}
            {rules.map((r, ruleIndex) => {
              const iconFn = Icon[r.presetIcon as keyof typeof Icon] ?? Icon.sparkle;
              const rowStats = stats?.byRule.find((b) => b.instanceId === r.instanceId);
              const prevRule = ruleIndex > 0 ? rules[ruleIndex - 1] : undefined;
              const eligibleAfterPrevious =
                stats && ruleIndex === 0
                  ? stats.game.fullPoolSize
                  : stats && prevRule
                    ? stats.byRule.find((b) => b.instanceId === prevRule.instanceId)?.remainingPoolSizeAfterRule ??
                      null
                    : null;
              return (
                <div
                  key={r.instanceId}
                  className="flex flex-col gap-2.5 overflow-hidden rounded-2xl"
                  style={{
                    background: c.surface,
                    border: `0.5px solid ${c.line}`,
                    padding: 14,
                  }}
                >
                  <button
                    type="button"
                    className="flex w-full flex-col gap-2.5 text-left"
                    onClick={() => setEditingInstanceId(r.instanceId)}
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px]"
                        style={{ width: 36, height: 36, background: c.surfaceAlt, color: c.ink }}
                      >
                        {iconFn(18, c.ink)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div
                          className="text-[17px] leading-tight"
                          style={{
                            fontFamily: typ.display,
                            fontWeight: typ.displayWeight,
                            letterSpacing: typ.displayLetter,
                            color: c.ink,
                          }}
                        >
                          {r.name}
                        </div>
                        {summarize(r) ? (
                          <div
                            className="mt-0.5 truncate font-mono text-[11px]"
                            style={{ color: c.inkSoft, fontFamily: typ.mono }}
                          >
                            {summarize(r)}
                          </div>
                        ) : null}
                      </div>
                      <div
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[13px] font-bold"
                        style={{
                          width: 28,
                          height: 28,
                          background: c.surfaceAlt,
                          color: c.inkSoft,
                          fontFamily: typ.body,
                        }}
                      >
                        i
                      </div>
                    </div>
                    <div
                      className="text-[12px] leading-[1.45]"
                      style={{ color: c.inkSoft, fontFamily: typ.body }}
                    >
                      {ruleShortDescription(r)}
                    </div>
                    {rowStats && eligibleAfterPrevious !== null && stats && !statsError
                      ? ruleCardImprovementFooter(
                          rowStats.remainingPoolSizeAfterRule,
                          eligibleAfterPrevious,
                          rowStats.method,
                          c,
                          typ,
                        )
                      : null}
                  </button>
                </div>
              );
            })}
          </div>

          {authLoaded && isSignedIn ? (
            <div
              className="rounded-2xl px-3.5 py-3.5"
              style={{ background: c.surface, border: `0.5px solid ${c.line}`, marginTop: 4 }}
            >
              <div className="mb-3 flex items-center gap-1.5">
                {Icon.sparkle(14, c.accent)}
                <div
                  className="text-[11px] font-bold uppercase tracking-[0.08em]"
                  style={{ color: c.ink, fontFamily: typ.body }}
                >
                  Filter stats
                </div>
                <div className="text-[11px] font-medium" style={{ color: c.inkMuted, fontFamily: typ.body }}>
                  · historical
                </div>
              </div>
              {statsLoading && !stats ? (
                <div className="text-[13px]" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                  Loading…
                </div>
              ) : null}
              {statsError ? (
                <div className="text-[13px]" style={{ color: c.lucky, fontFamily: typ.body }}>
                  {statsError}
                </div>
              ) : null}
              {stats && !statsError ? (
                <>
                  <div className="flex items-baseline justify-between py-1.5">
                    <span className="text-[13px]" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                      Full pool
                    </span>
                    <span className="font-mono text-[13px] font-semibold" style={{ color: c.ink, fontFamily: typ.mono }}>
                      1 : {stats.game.fullPoolSize.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-px opacity-50" style={{ background: c.line }} />
                  <div className="flex items-baseline justify-between pb-1.5 pt-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[13px] font-semibold" style={{ color: c.ink, fontFamily: typ.body }}>
                        All active rules
                      </span>
                      <span
                        className="rounded px-1 py-px font-mono text-[9px] font-bold uppercase tracking-[0.08em]"
                        style={{ background: c.surfaceAlt, color: c.inkMuted }}
                      >
                        {stats.combined.method}
                      </span>
                    </div>
                    <span className="font-mono text-[14px] font-bold" style={{ color: c.accent, fontFamily: typ.mono }}>
                      1 : {stats.combined.remainingPoolSize.toLocaleString()}
                    </span>
                  </div>
                  <div className="rounded-[10px] p-2.5" style={{ background: c.surfaceAlt }}>
                    <div
                      className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em]"
                      style={{ color: c.inkMuted, fontFamily: typ.body }}
                    >
                      Combined draws matched
                    </div>
                    <div className="flex gap-2">
                      {([50, 100, 500] as const).map((w) => (
                        <div
                          key={w}
                          className="flex-1 rounded-lg px-1.5 py-2 text-center"
                          style={{
                            background: c.bg,
                            border: `0.5px solid ${c.line}`,
                          }}
                        >
                          <div
                            className="font-mono text-[10px] font-semibold"
                            style={{ color: c.inkMuted, fontFamily: typ.mono }}
                          >
                            last {w}
                          </div>
                          <div
                            className="mt-0.5 text-[22px] leading-tight"
                            style={{
                              fontFamily: typ.display,
                              fontWeight: typ.displayWeight,
                              color: c.ink,
                              letterSpacing: typ.displayLetter,
                            }}
                          >
                            {stats.combined.matches[String(w)] ?? 0}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          ) : authLoaded ? (
            <div className="px-0.5 text-[12px]" style={{ color: c.inkMuted, fontFamily: typ.body }}>
              Sign in to load historical rule stats from the API.
            </div>
          ) : null}

          {rulePresets.length > 0 && (
            <>
              <div
                className="px-0.5 pt-1.5 text-xs font-medium uppercase tracking-[0.08em]"
                style={{ color: c.inkMuted, fontFamily: typ.body }}
              >
                Add a rule
              </div>
              <div className="px-0.5 text-[12px] leading-snug" style={{ color: c.inkSoft, fontFamily: typ.body }}>
                Choose from the rule types below (more coming soon).
              </div>
              {available.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {available.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="flex cursor-pointer items-center gap-1.5 rounded-full border border-dashed px-3 py-2"
                      style={{ borderColor: c.line, fontFamily: typ.body }}
                      onClick={() => addRule(p.id as RuleId)}
                    >
                      {(Icon[p.icon as keyof typeof Icon] ?? Icon.plus)(14, c.inkSoft)}
                      <span className="text-[13px] font-medium" style={{ color: c.ink }}>
                        {p.name}
                      </span>
                      {Icon.plus(14, c.inkMuted)}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border px-3 py-2.5 text-[12px]" style={{ borderColor: c.line, color: c.inkMuted, fontFamily: typ.body }}>
                  All available rule types are already in your stack.
                </div>
              )}
            </>
          )}

          <div className="px-5 text-center text-[11px] leading-snug" style={{ color: c.inkMuted, fontFamily: typ.body }}>
            Lottery outcomes are random. Rules shape which numbers get picked — they don&apos;t improve odds.
          </div>

          {editingRule && editingInstanceId && (
            <RuleDetailSheet
              rule={editingRule}
              c={c}
              typ={typ}
              ruleStats={stats?.byRule.find((b) => b.instanceId === editingRule.instanceId) ?? null}
              statsLoading={statsLoading}
              statsRefreshing={sheetStatsRefreshing}
              onRefreshStats={isSignedIn ? refreshStatsWithDraftSettings : undefined}
              onClose={() => setEditingInstanceId(null)}
              onSave={(patch) => {
                updateRule(editingInstanceId, patch);
              }}
              onRemove={() => {
                removeRule(editingInstanceId);
                setEditingInstanceId(null);
              }}
            />
          )}
            </div>
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default RulesPage;
